import React, { useState } from 'react';
import { gql, useMutation, ApolloError } from '@common/hooks';
import { Button } from 'antd';
import {
  createMessage,
  mutationResponseMessage,
} from 'works/legacy/lib/message-helper';
import { WorksListItemType } from '@onboarding/graphql';

const PERFORM_WORKS_LIST_ITEM_ACTION = gql`
  mutation PerformWorksListItemAction(
    $id: ID!
    $input: PerformWorksListItemActionInput!
  ) {
    performWorksListItemAction(id: $id, input: $input) {
      worksListItem {
        id
        state
        compliantForAssignment
        revisedCompliantForAssignment
        expiresOn
        revisedExpiresOn
        revisedExpired
        isRevisedExpiringSoon
        worksList {
          id
          completedItemsCount
          totalItemsCount
        }
      }
    }
  }
`;

type Props = {
  worksList: {
    id: string;
    canApproveReject: {
      value: boolean;
    };
    worksListItems: WorksListItemType[];
  };
};

export function ApproveAllItemsButton({ worksList }: Props) {
  const [performAction] = useMutation(PERFORM_WORKS_LIST_ITEM_ACTION);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [allItemsApproved, setAllItemsApproved] = useState(false);
  const worksListItems = worksList.worksListItems;

  if (!worksList.canApproveReject.value) return null;

  const allWorksItemsApproved =
    worksListItems &&
    worksListItems.filter((item) => {
      if (item.state === 'approved') return item;
    }).length === worksListItems.length;

  if (allWorksItemsApproved) {
    if (allItemsApproved !== true) setAllItemsApproved(true);
  } else {
    if (allItemsApproved !== false) setAllItemsApproved(false);
  }

  const itemHasNoFiles = (item: WorksListItemType) => {
    return !item.detailItem?.detailItemAttachments?.length;
  };

  const itemApprovable = (item: WorksListItemType) => {
    if (['approved', 'pending'].includes(item.state)) return false;
    if (itemHasNoFiles(item)) return false;
    return true;
  };

  const allItemsApprovable =
    worksListItems &&
    worksListItems.filter((item) => {
      if (itemApprovable(item)) return true;
    }).length !== 0;

  const approveAllItems = () => {
    setButtonLoading(true);

    Promise.all(
      worksListItems.map((item) => {
        if (!itemApprovable(item)) return;
        return performAction({
          variables: {
            id: item.id,
            input: {
              action: 'approve',
            },
          },
        });
      })
    )
      .then((responsesArray) => {
        const validResponses = responsesArray.filter((response) => {
          return response;
        });
        setButtonLoading(false);

        if (!validResponses || validResponses.length === 0) return;
        mutationResponseMessage({
          gqlResponse: validResponses[0] as {
            data?: unknown;
            errors?: ApolloError[] | undefined;
          },
          onSuccess: 'Compliance items successfully approved',
          errorMessage: 'Failed to approve compliance item status',
        });
      })
      .catch((error) => {
        setButtonLoading(false);
        console.warn('Approve all error:', error);
        createMessage('error', 'Failed to approve compliance item status');
      });
  };

  return (
    <>
      {allItemsApprovable && !allItemsApproved && (
        <Button
          type="primary"
          loading={buttonLoading}
          onClick={approveAllItems}
          disabled={allItemsApproved}
        >
          Approve All Items
        </Button>
      )}
    </>
  );
}

ApproveAllItemsButton.fragments = {
  approveAllItemsButtonInfo: gql`
    fragment ApproveAllItemsButtonInfo on WorksList {
      id
      canApproveReject {
        value
      }
      worksListItems {
        id
        state
        detailItem {
          ... on License {
            id
            detailItemAttachments {
              id
            }
          }
          ... on NurseAssessment {
            id
            detailItemAttachments {
              id
            }
          }
          ... on NurseHealthDoc {
            id
            detailItemAttachments {
              id
            }
          }
          ... on NurseCertification {
            id
            detailItemAttachments {
              id
            }
          }
          ... on Document {
            id
            detailItemAttachments {
              id
            }
          }
        }
      }
    }
  `,
};
